import { defineStore } from "pinia";
import { useWebhusetApi } from "~/composables/webhuset-rest-api";
import type { AuthenticateResult, CustomerEntityType } from "../types/index";
import { routeHasMiddleware } from "~/helpers/routeHelpers";

export const useAuthStore = defineStore(
  "auth",
  () => {
    const { restGet } = useWebhusetApi();
    const config = useRuntimeConfig();

    const apiKey = ref<string>();
    const customerId = ref<number>();
    const customerName = ref<string>();
    const partnerName = ref<string>();
    const customerEntityType = ref<CustomerEntityType>();
    const isSmsVerified = ref(false);
    const codeTransactionId = ref<number>();
    const isPartner = ref(false);
    const partnerId = ref<number>();

    const saveCustomerState: (data: {
      apiKey: string;
      customerId: number;
      customerName: string;
      entityType: CustomerEntityType;
      smsVerified: boolean;
    }) => void = (data) => {
      apiKey.value = data.apiKey;
      customerId.value = data.customerId;
      customerName.value = data.customerName;
      customerEntityType.value = data.entityType;
      isSmsVerified.value = data.smsVerified;
      isPartner.value = false;
    };

    const savePartnerState: (data: {
      apiKey: string;
      customerId?: number;
      customerName?: string;
      partnerName: string;
      partnerId?: number;
    }) => void = (data) => {
      console.log("savePartnerState", data);
      apiKey.value = data.apiKey;
      customerId.value = data.customerId;
      customerName.value = data.customerName;
      isSmsVerified.value = true;
      partnerName.value = data.partnerName;
      isPartner.value = true;
      partnerId.value = data.partnerId;
    };

    const impersonateCustomer = (data: {
      customerId: number;
      customerName: string;
    }) => {
      if (!isPartner.value) {
        console.error(
          "Can only impersonate customers when logged in as partner"
        );
      }

      customerId.value = data.customerId;
      customerName.value = data.customerName;
    };

    const loginCustomer = async (username: string, password: string) => {
        const encodedUsername = encodeURIComponent(username);
        const encodedPassword = encodeURIComponent(password);
        const response = await restGet<AuthenticateResult>(
        `/customer/contact/authenticate?username=${encodedUsername}&password=${encodedPassword}&company_profile_id=${config.public.companyProfileId}`,
        true
      );

      saveCustomerState({
        apiKey: response.apikey,
        customerId: response.customer_id,
        customerName: response.customer_name,
        entityType: response.customer_entity_type,
        smsVerified: response.is_sms_verified,
      });
    };

    return {
      loginCustomer,
      isAuthenticated: computed(() => !!apiKey.value),
      apiKey,
      customerName,
      customerEntityType,
      customerId,
      isPartner,
      partnerName,
      partnerId,
      isSmsVerified,
      codeTransactionId,
      saveCustomerState,
      savePartnerState,
      impersonateCustomer,
      clearImpersonation: () => {
        if (!isPartner.value) {
          console.error(
            "Can only impersonate customers when logged in as partner"
          );
          return;
        }

        customerId.value = undefined;
        customerName.value = undefined;
      },
      setSmsVerified: (transactionId?: number) => {
        isSmsVerified.value = true;
        codeTransactionId.value = transactionId;
      },
      logout: async (apiKeyExpired: boolean = false) => {
        const route = useRoute();

        apiKey.value = undefined;
        customerName.value = undefined;
        customerId.value = undefined;
        isSmsVerified.value = false;
        codeTransactionId.value = undefined;
        customerEntityType.value = undefined;
        isPartner.value = false;

        if (routeHasMiddleware(route, "require-auth")) {
          await navigateTo({
            path: "/logg-inn",
            query: {
              sessionExpired: apiKeyExpired ? "true" : undefined,
            },
          });
        }
      },
    };
  },
  {
    persist: true,
  }
);
